import { createSeedState } from '../data/seed';
import type { RegistryState } from '../data/types';
import type { RegistryAction } from './actions';
import {
  rejectApplication,
  requestIssue,
  saveInvestorApplication,
  sendApplicationReminder,
  setApplicationStatus,
} from './handlers/applicationHandlers';
import { startApplicationFromAllocation } from './handlers/allocationHandlers';
import { decideApproval } from './handlers/approvalHandlers';
import {
  issueDefaultNotice,
  proposeDefaultRemedy,
  recordRemittance,
  sendCommunication,
  updatePaymentException,
  waiveDefaultInterest,
} from './handlers/exceptionHandlers';
import {
  payCapitalReturn,
  payRedemptionRun,
  priceRedemptionRun,
  saveCapitalReturn,
  submitCapitalReturn,
  submitRedemptionRun,
} from './handlers/lifecycleHandlers';
import {
  createCall,
  ignoreBankLine,
  investorPayApplication,
  investorPayCall,
  matchBankLine,
  payDistribution,
  saveDistribution,
  sendCallReminders,
  submitDistribution,
} from './handlers/moneyHandlers';
import {
  advanceAtoReport,
  advanceChangeRequest,
  advanceMigration,
  advanceTaxBatch,
  createChangeRequest,
  createMigration,
  createTaxBatch,
  updateHolderRecord,
  updateKyc,
} from './handlers/recordHandlers';
import { requestLedgerReversal } from './handlers/ledgerHandlers';
import { importUnitPrices, setUnitPrice } from './handlers/priceHandlers';
import {
  activateSetup,
  chooseSetupContact,
  completeSetupSection,
  discardSetup,
  publishSetup,
  requestPublish,
  startSetup,
  updateHouseDefaults,
  updateSetup,
} from './handlers/setupHandlers';
import { overrideWholesale, recordRiskReview, verifySourceOfWealth } from './handlers/kycHandlers';
import { submitReportRun } from './handlers/reportHandlers';
import {
  issuePaymentInstruction,
  recordReceipt,
  regenerateInstruction,
  releaseCertificates,
  saveIssueRun,
  sendPaymentReminder,
  submitIssueRun,
} from './handlers/raiseHandlers';
import { updateStaffProfile } from './handlers/staffHandlers';
import { completeTask } from './handlers/taskHandlers';

export function registryReducer(state: RegistryState, action: RegistryAction): RegistryState {
  switch (action.type) {
    case 'RESET':
      return createSeedState();
    case 'REPLACE_STATE':
      return action.state;
    case 'SWITCH_TENANT': {
      const user = state.users.find((candidate) => candidate.tenantId === action.tenantId);
      return {
        ...state,
        currentTenantId: action.tenantId,
        currentUserId: user?.id ?? state.currentUserId,
      };
    }
    case 'SWITCH_USER':
      return { ...state, currentUserId: action.userId };
    case 'SWITCH_INVESTOR':
      return { ...state, currentInvestorLoginId: action.loginId };
    case 'APPLICATION_SET_STATUS':
      return setApplicationStatus(state, action);
    case 'APPLICATION_REQUEST_ISSUE':
      return requestIssue(state, action);
    case 'APPLICATION_REJECT':
      return rejectApplication(state, action);
    case 'APPLICATION_SEND_REMINDER':
      return sendApplicationReminder(state, action);
    case 'APPLICATION_START_FROM_ALLOCATION':
      return startApplicationFromAllocation(state, action);
    case 'INVESTOR_APPLICATION_SAVE': {
      const next = saveInvestorApplication(state, action);
      const id = action.application.id ?? next.applications[0]?.id;
      return action.submit && id ? issuePaymentInstruction(next, id) : next;
    }
    case 'APPROVAL_DECIDE':
      return decideApproval(state, action);
    case 'DISTRIBUTION_SAVE':
      return saveDistribution(state, action);
    case 'DISTRIBUTION_SUBMIT':
      return submitDistribution(state, action);
    case 'DISTRIBUTION_PAY':
      return payDistribution(state, action);
    case 'CALL_CREATE':
      return createCall(state, action);
    case 'CALL_SEND_REMINDERS':
      return sendCallReminders(state, action);
    case 'BANK_LINE_MATCH':
      return matchBankLine(state, action);
    case 'BANK_LINE_IGNORE':
      return ignoreBankLine(state, action);
    case 'INVESTOR_PAY_CALL':
      return investorPayCall(state, action);
    case 'INVESTOR_PAY_APPLICATION':
      return investorPayApplication(state, action);
    case 'KYC_UPDATE':
      return updateKyc(state, action);
    case 'CHANGE_REQUEST_ADVANCE':
      return advanceChangeRequest(state, action);
    case 'CHANGE_REQUEST_CREATE':
      return createChangeRequest(state, action);
    case 'TAX_BATCH_ADVANCE':
      return advanceTaxBatch(state, action);
    case 'TAX_BATCH_CREATE':
      return createTaxBatch(state, action);
    case 'ATO_REPORT_ADVANCE':
      return advanceAtoReport(state, action);
    case 'TASK_COMPLETE':
      return completeTask(state, action);
    case 'MIGRATION_CREATE':
      return createMigration(state, action);
    case 'MIGRATION_ADVANCE':
      return advanceMigration(state, action);
    case 'HOLDER_UPDATE':
      return updateHolderRecord(state, action);
    case 'UNIT_PRICES_IMPORT':
      return importUnitPrices(state, action);
    case 'UNIT_PRICE_SET':
      return setUnitPrice(state, action);
    case 'CAPITAL_RETURN_SAVE':
      return saveCapitalReturn(state, action);
    case 'CAPITAL_RETURN_SUBMIT':
      return submitCapitalReturn(state, action);
    case 'CAPITAL_RETURN_PAY':
      return payCapitalReturn(state, action);
    case 'REDEMPTION_RUN_PRICE':
      return priceRedemptionRun(state, action);
    case 'REDEMPTION_RUN_SUBMIT':
      return submitRedemptionRun(state, action);
    case 'REDEMPTION_RUN_PAY':
      return payRedemptionRun(state, action);
    case 'CALL_DEFAULT_NOTICE':
      return issueDefaultNotice(state, action);
    case 'CALL_DEFAULT_WAIVE':
      return waiveDefaultInterest(state, action);
    case 'CALL_DEFAULT_REMEDY':
      return proposeDefaultRemedy(state, action);
    case 'PAYMENT_EXCEPTION_UPDATE':
      return updatePaymentException(state, action);
    case 'REMITTANCE_RECORD':
      return recordRemittance(state, action);
    case 'LEDGER_REVERSAL_REQUEST':
      return requestLedgerReversal(state, action);
    case 'SETUP_START':
      return startSetup(state, action);
    case 'SETUP_UPDATE':
      return updateSetup(state, action);
    case 'SETUP_CHOOSE_CONTACT':
      return chooseSetupContact(state, action);
    case 'SETUP_COMPLETE_SECTION':
      return completeSetupSection(state, action);
    case 'SETUP_ACTIVATE':
      return activateSetup(state, action);
    case 'SETUP_PUBLISH':
      return publishSetup(state, action);
    case 'SETUP_DISCARD':
      return discardSetup(state, action);
    case 'SETUP_REQUEST_PUBLISH':
      return requestPublish(state, action);
    case 'HOUSE_DEFAULTS_UPDATE':
      return updateHouseDefaults(state, action);
    case 'STAFF_PROFILE_UPDATE':
      return updateStaffProfile(state, action);
    case 'APPLICATION_RECORD_RECEIPT':
      return recordReceipt(state, action);
    case 'APPLICATION_REGENERATE_INSTRUCTION':
      return regenerateInstruction(state, action);
    case 'APPLICATION_PAYMENT_REMINDER':
      return sendPaymentReminder(state, action);
    case 'ISSUE_RUN_SAVE':
      return saveIssueRun(state, action);
    case 'ISSUE_RUN_SUBMIT':
      return submitIssueRun(state, action);
    case 'ISSUE_RUN_RELEASE_CERTIFICATES':
      return releaseCertificates(state, action);
    case 'HOLDER_RISK_REVIEW':
      return recordRiskReview(state, action);
    case 'HOLDER_SOW_VERIFY':
      return verifySourceOfWealth(state, action);
    case 'HOLDER_WHOLESALE_OVERRIDE':
      return overrideWholesale(state, action);
    case 'REPORT_RUN_SUBMIT':
      return submitReportRun(state, action);
    case 'COMMUNICATION_SEND':
      return sendCommunication(state, action);
    default:
      return state;
  }
}
